import { Devices, Person } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { SolaForceDataGrid } from '~/components/SolaForceDataGrid';
import { useAllOrgUnits } from '~/data/orgunits';
import {
  AnalyticsReportDefaultProps,
  AnalyticsReportRoleScope,
  AnalyticsReportScope,
  AnalyticsReportTypes,
  useEmployeeEquipmentReportData,
} from '~/data/reports';
import { Roles } from '~/data/user';
import {
  ReportColumnsVisibility,
  ReportDataExport,
  ReportDatasetLayout,
  ReportDatasetRowCount,
  ReportDatasetSelector,
  ReportViewLayout,
  useReportDataset,
} from '~/scenes/PortalModules/Analytics/common';
import { useEmployeeEquipmentReport } from '~/scenes/PortalModules/Analytics/People/EmployeeEquipmentReport/useEmployeeEquipmentReport';
import { hasOneOfRoles } from '~/services/Permissions';

export const EmployeeEquipmentReport = () => {
  const { t } = useTranslation();

  const apiRef = useGridApiRef();

  const loggedInEmployeeId = useLoggedInEmployeeId();
  const loggedInUserRoles = useLoggedInUserRoles();
  const { orgUnits } = useAllOrgUnits();

  const { reportUnitOptions, disableUnitOptionsValidation, canViewCompany } =
    useMemo(() => {
      const isITAdmin = hasOneOfRoles(loggedInUserRoles, [Roles.IT_ADMIN]);
      const isHRAdmin = hasOneOfRoles(loggedInUserRoles, [Roles.HR_ADMIN]);

      const canViewCompany = isITAdmin || isHRAdmin;

      const resolveUnitOptions = () => {
        if (canViewCompany) {
          return orgUnits.map((u) => u.fOrgUnitId);
        }

        return undefined;
      };

      return {
        canViewCompany,
        reportUnitOptions: resolveUnitOptions(),
        disableUnitOptionsValidation: canViewCompany,
      };
    }, [loggedInUserRoles, orgUnits]);

  const defaultReportSelection: AnalyticsReportDefaultProps<object> = {
    reportType: {
      type: AnalyticsReportTypes.Team,
      role: AnalyticsReportRoleScope.Manager,
      employeeId: loggedInEmployeeId,
      scope: AnalyticsReportScope.Direct,
    },
  };

  const reportDatasetProps = useReportDataset({
    selection: defaultReportSelection,
    config: {
      options: {
        types: [
          ...(canViewCompany ? [AnalyticsReportTypes.Company] : []),
          AnalyticsReportTypes.Team,
          AnalyticsReportTypes.HR,
          AnalyticsReportTypes.Unit,
        ],
        units: reportUnitOptions,
      },
      disableUnitOptionsValidation,
    },
  });

  const {
    data,
    isLoading: isLoadingReportData,
    failedToLoad,
  } = useEmployeeEquipmentReportData(reportDatasetProps.reportSelection);

  const { table, counts } = useEmployeeEquipmentReport({ data });

  return (
    <ReportViewLayout
      heading={t('laEmployeesEquipment')}
      slots={{
        toolbar: {
          main: (
            <ReportDatasetLayout showHeading={true}>
              <ReportDatasetSelector
                {...reportDatasetProps}
                isLoading={isLoadingReportData}
                failedToLoad={failedToLoad}
              />
              <ReportDatasetRowCount
                count={counts.employees}
                description={t('laEmployees')}
                icon={<Person />}
              />
              <ReportDatasetRowCount
                count={counts.equipment}
                description={t('equipment')}
                icon={<Devices />}
              />
            </ReportDatasetLayout>
          ),
          actions: (
            <>
              <ReportColumnsVisibility
                apiRef={apiRef}
                columnGroupingModel={table.columnGroupingModel}
                isLoading={isLoadingReportData}
              />
              <ReportDataExport
                title={t('laEmployeesEquipment')}
                apiRef={apiRef}
                isLoading={isLoadingReportData}
              />
            </>
          ),
        },
      }}
    >
      <SolaForceDataGrid
        apiRef={apiRef}
        columns={table.columnConfigs}
        columnGroupingModel={table.columnGroupingModel}
        experimentalFeatures={{ columnGrouping: true }}
        rows={table.rows}
        density={'compact'}
        getRowId={(row) => row.rowId}
        pinnedColumns={{
          left: ['employeeId', 'firstName', 'lastName'],
        }}
      />
    </ReportViewLayout>
  );
};
